import {
	APP_MODE,
	APP_MODE_SETTINGS
}                           from '@app/_app-config/app-mode-config';
import { KEYCLOACK_CONFIG } from '@appConfig/keycloak-config';
import { OPENAPI_CONFIG }   from '@appConfig/openApi-config';
import {
	ApiUrlSegmentEnum,
	RouteBasePath
}                           from '@core/interfaces/route.interface';
import { environment }      from '@env/environment';
import { EnvironmentEnum }  from '@env/environment.model';
import { SvgTypeEnum }      from '@shared/components/svg/svg.model';

const locationOrigin = window.location.origin;

// NEW ENVS
const UNSTABLE_URL = KEYCLOACK_CONFIG.useKeycloakService ? environment?.unstableBaseUrl : 'http://localhost:3000';
const baseUrl = locationOrigin.includes( 'localhost' ) ? UNSTABLE_URL : locationOrigin;

let appMode = locationOrigin.includes( 'localhost' )
							? APP_MODE.LOCAL
							: environment.activeEnvironment === EnvironmentEnum.UNSTABLE
								? APP_MODE.DEVELOP
								: APP_MODE.PRODUCTION;

export const SWITCH_APP_MODE = ( newAppMode: APP_MODE ) => {
	appMode = newAppMode;
};

export function GET_APP_MODE(): APP_MODE { return appMode; }

export const APP_CONFIG = {
	baseUrl,
	appMode: GET_APP_MODE(),
	// UI
	authentication: {
		api: {
			path: 'management/',
			endpoint: 'authentication',
			useLocalPath: false,
			useMapping: false,
			mappingOutgoingData: []
		}
	},
	navbar: APP_MODE_SETTINGS[ appMode ].navbar,
	openApi: OPENAPI_CONFIG,
	services: {
		tssRequest: {
			isUnavailable: false,
			i18nReason: 'cspUpdate'
		}
	},
	roles: {
		api: {
			path: `${ ApiUrlSegmentEnum.USER_MANAGEMENT }/`,
			endpoint: 'roles',
			useLocalPath: false,
			useMapping: false,
			mappingOutgoingData: []
		}
	},
	ui: {
		store: {
			id: 'ui'
		}
	},
	// ENTITIES
	accountTenant: {
		route: RouteBasePath.Tenant,
		routeIsActive: false,
		entityId: 'tenants',
		store: {
			id: 'admin-tenant',
			sortByProperty: 'name'
		},
		i18nPath: 'admin',
		sidebar: {
			order: 4,
			title: 'admin',
			icon: SvgTypeEnum.ACCOUNTS,
			isSvgIcon: true,
			isActive: APP_MODE_SETTINGS[ appMode ].sidebar.admin.isActive,
			isVisible: APP_MODE_SETTINGS[ appMode ].sidebar.admin.isVisible
		}
	},
	admin: {
		route: RouteBasePath.Tenant,
		routeIsActive: true,
		entityId: 'tenants',
		store: {
			id: 'admin-tenant',
			sortByProperty: 'name'
		},
		i18nPath: 'admin',
		sidebar: {
			order: 4,
			title: 'admin',
			icon: SvgTypeEnum.ACCOUNTS,
			isSvgIcon: true,
			isActive: APP_MODE_SETTINGS[ appMode ].sidebar.admin.isActive,
			isVisible: APP_MODE_SETTINGS[ appMode ].sidebar.admin.isVisible
		}
	},
	adminConsole: {
		route: RouteBasePath.AdminConsole,
		routeIsActive: true,
		entityId: 'adminConsole',
		i18nPath: 'adminConsole',
		sidebar: {
			order: 1,
			title: 'adminConsole',
			icon: 'icon-star',
			isActive: APP_MODE_SETTINGS[ appMode ].sidebar.adminConsole.isActive,
			isVisible: APP_MODE_SETTINGS[ appMode ].sidebar.adminConsole.isVisible
		}
	},
	archiving: {
		route: RouteBasePath.ArchivingImports,
		routeIsActive: false,
		entityId: 'archiving',
		store: {
			id: 'archiving',
			sortByProperty: 'businessDate',
			sortOrder: 'desc'
		},
		i18nPath: 'archiving',
		api: {
			path: `${ ApiUrlSegmentEnum.ARCHIVING }/`,
			endpoint: ApiUrlSegmentEnum.ARCHIVING,
			useLocalPath: false,
			useMapping: false,
			mappingOutgoingData: []
		}
	},
	archivingContracts: {
		entityId: 'contracts',
		store: {
			id: 'archivingContract',
			sortByProperty: 'lastModifiedDate',
			sortOrder: 'desc'
		},
		i18nPath: 'contract'
	},
	archivingExport: {
		routeIsActive: false,
		entityId: 'archivingExport',
		store: {
			id: 'archivingExport',
			sortByProperty: 'created',
			sortOrder: 'desc'
		},
		i18nPath: 'archivingExport'
	},
	archivingExportItem: {
		routeIsActive: false,
		entityId: 'archivingExportItem',
		store: {
			id: 'archivingExportItem',
			sortByProperty: 'created',
			sortOrder: 'desc'
		},
		i18nPath: 'archivingExportItem'
	},
	archivingKeyword: {
		entityId: 'keyword',
		store: {
			id: 'archivingKeyword',
			sortByProperty: 'name',
			sortOrder: 'asc'
		},
		i18nPath: 'keyword'
	},
	archivingProceduralDocument: {
		routeIsActive: false,
		entityId: 'archivingProceduralDocument',
		store: {
			id: 'archivingProceduralDocument',
			sortByProperty: 'created',
			sortOrder: 'desc'
		},
		api: {
			path: `${ ApiUrlSegmentEnum.ARCHIVING_API }/`,
			endpoint: ApiUrlSegmentEnum.PROCEDURAL_DOCUMENTATIONS,
			entity: 'documents',
			useLocalPath: false,
			useMapping: false,
			mappingOutgoingData: []
		},
		i18nPath: 'archivingProceduralDocument'
	},
	archivingProceduralDocumentation: {
		routeIsActive: false,
		entityId: 'archivingProceduralDocumentation',
		store: {
			id: 'archivingProceduralDocumentation',
			sortByProperty: 'created',
			sortOrder: 'desc'
		},
		i18nPath: 'archivingProceduralDocumentation'
	},
	archivingProceduralDocumentationExport: {
		routeIsActive: false,
		entityId: 'archivingProceduralDocumentationExport',
		store: {
			id: 'archivingProceduralDocumentationExport',
			sortByProperty: 'created',
			sortOrder: 'desc'
		},
		i18nPath: 'archivingProceduralDocumentationExport'
	},
	archivingTssLogs: {
		route: RouteBasePath.ArchivingTssLogs,
		routeIsActive: false,
		entityId: 'archivingTssLogs',
		store: {
			id: 'archivingTssLogs',
			sortByProperty: 'businessDate',
			sortOrder: 'desc'
		},
		i18nPath: 'archivingTssLogs'
	},
	archivingTssLogExport: {
		routeIsActive: false,
		entityId: 'archivingTssLogExport',
		store: {
			id: 'archivingTssLogExport',
			sortByProperty: 'created',
			sortOrder: 'desc'
		},
		i18nPath: 'archivingTssLogExport'
	},
	businesspremises: {
		route: RouteBasePath.BusinessPremises,
		routeIsActive: true,
		entityId: 'businessPremises',
		store: {
			id: 'businessPremises',
			sortByProperty: 'lastModifiedDate',
			sortOrder: 'desc'
		},
		i18nPath: 'businessPremises',
		sidebar: {
			order: 6,
			title: 'businessPremises',
			icon: SvgTypeEnum.BUSINESS_PREMISES,
			isSvgIcon: true,
			isActive: APP_MODE_SETTINGS[ appMode ].sidebar.businesspremises.isActive,
			isVisible: APP_MODE_SETTINGS[ appMode ].sidebar.businesspremises.isVisible,
		},
		api: {
			path: `${ ApiUrlSegmentEnum.MANAGEMENT }/`,
			endpoint: ApiUrlSegmentEnum.BUSINESS_PREMISES,
			useLocalPath: false,
			useMapping: false,
			mappingOutgoingData: [
				{ from: 'parentOrgId', to: 'storeGroupId' }
			]
		}
	},
	cashPointClosing: {
		routeIsActive: false,
		entityId: 'cashPointClosing',
		store: {
			id: 'cashPointClosing',
			sortByProperty: 'businessDate',
			sortOrder: 'desc'
		},
		i18nPath: 'cashPointClosing'
	},
	clients: {
		route: RouteBasePath.Clients,
		routeIsActive: true,
		entityId: 'clients',
		store: {
			id: 'client',
			sortByProperty: 'lastModifiedDate',
			sortOrder: 'desc'
		},
		i18nPath: 'client',
		sidebar: {
			order: 11,
			title: 'clients',
			icon: 'icon-folder-alt',
			isActive: APP_MODE_SETTINGS[ appMode ].sidebar.clients.isActive,
			isVisible: APP_MODE_SETTINGS[ appMode ].sidebar.clients.isVisible
		},
		api: {
			path: `${ ApiUrlSegmentEnum.CLIENTS }/`,
			endpoint: ApiUrlSegmentEnum.CLIENTS,
			useLocalPath: false,
			useMapping: false,
			mappingOutgoingData: []
		}
	},
	configuration: {
		route: RouteBasePath.Configuration,
		routeIsActive: true,
		entityId: 'configuration',
		i18nPath: 'configuration',
		store: {
			id: 'configuration'
		},
		sidebar: {
			order: 14,
			title: 'configuration',
			icon: 'icon-settings',
			isActive: APP_MODE_SETTINGS[ appMode ].sidebar.configuration.isActive,
			isVisible: APP_MODE_SETTINGS[ appMode ].sidebar.configuration.isVisible
		}
	},
	contracts: {
		route: RouteBasePath.Contracts,
		routeIsActive: true,
		sidebar: {
			order: 2,
			title: 'contracts',
			icon: SvgTypeEnum.CONTRACT,
			isSvgIcon: true,
			isActive: APP_MODE_SETTINGS[ appMode ].sidebar.contracts.isActive,
			isVisible: APP_MODE_SETTINGS[ appMode ].sidebar.contracts.isVisible,
		}
	},
	currentUser: {
		route: RouteBasePath.CURRENT_USER,
		routeIsActive: true,
		entityId: 'users',
		store: {
			id: 'currentUser',
			sortByProperty: 'lastModifiedDate',
			sortOrder: 'desc'
		},
		i18nPath: 'user'
	},
	dashboard: {
		route: RouteBasePath.TenantConsole,
		routeIsActive: true,
		sidebar: {
			order: 1,
			title: 'tenantConsole',
			icon: 'icon-pie-chart',
			isActive: APP_MODE_SETTINGS[ appMode ].sidebar.dashboard.isActive,
			isVisible: APP_MODE_SETTINGS[ appMode ].sidebar.dashboard.isVisible
		}
	},
	elsterContracts: {
		entityId: 'contracts',
		store: {
			id: 'elsterContract',
			sortByProperty: 'lastModifiedDate',
			sortOrder: 'desc'
		},
		i18nPath: 'contract'
	},
	encryptionKeys: {
		route: RouteBasePath.EncryptionKeys,
		routeIsActive: false,
		entityId: 'encryptionKeys',
		store: {
			id: 'encryptionKeys',
			sortByProperty: 'createdDate',
			sortOrder: 'desc'
		},
		i18nPath: 'keys',
		// TODO EncryptionKeys has no own page (will probably change in future)
		// sidebar: {
		// 	order: 8,
		// 	title: 'keys',
		// 	icon: 'icon-cloud-upload',
		// 	isActive: APP_MODE_SETTINGS[ appMode ].sidebar.keys.isActive,
		// 	isVisible: APP_MODE_SETTINGS[ appMode ].sidebar.keys.isVisible
		// },
		api: {
			path: `${ ApiUrlSegmentEnum.KEYS }/`,
			endpoint: ApiUrlSegmentEnum.KEYS,
			useLocalPath: false,
			useMapping: false,
			mappingOutgoingData: []
		}
	},
	ers: {
		enforceErsLink: false,
		route: RouteBasePath.Ers,
		routeIsActive: true,
		entityId: 'ers',
		store: {
			id: 'ers',
			sortByProperty: 'lastModifiedDate',
			sortOrder: 'desc'
		},
		i18nPath: 'ers',
		sidebar: {
			order: 8,
			title: 'ers',
			icon: SvgTypeEnum.ERS,
			isSvgIcon: true,
			isActive: APP_MODE_SETTINGS[ appMode ].sidebar.ers.isActive,
			isVisible: APP_MODE_SETTINGS[ appMode ].sidebar.ers.isVisible
		},
		api: {
			path: `${ ApiUrlSegmentEnum.MANAGEMENT }/`,
			endpoint: ApiUrlSegmentEnum.ERS,
			useLocalPath: false,
			useMapping: false,
			mappingOutgoingData: []
		}
	},
	fiskalContracts: {
		entityId: 'contracts',
		store: {
			id: 'fiskalContract',
			sortByProperty: 'lastModifiedDate',
			sortOrder: 'desc'
		},
		i18nPath: 'contract'
	},
	fiskalTssConfig: {
		routeIsActive: false,
		entityId: 'fiskalTssConfig',
		i18nPath: 'fiskalTssConfig'
	},
	fiskalTssLogs: {
		route: RouteBasePath.FiskalTssLogs,
		routeIsActive: false,
		entityId: 'fiskalTssLogs',
		store: {
			id: 'fiskalTssLogs',
			sortByProperty: 'uploadTime',
			sortOrder: 'desc'
		},
		i18nPath: 'fiskalTssLogs'
	},
	fccVersions: {
		route: RouteBasePath.FccVersions,
		entityId: 'fccVersion',
		store: {
			id: 'fccVersion',
			sortByProperty: 'version',
			sortOrder: 'asc'
		},
		i18nPath: 'fccVersion'
	},
	fccDatacenter: {
		entityId: 'fccDatacenter',
		i18nPath: 'fcc.fccDatacenter'
	},
	fccOpenTransactions: {
		entityId: 'fccOpenTransaction',
		i18nPath: 'fcc.fccOpenTransaction'
	},
	globalDashboard: {
		routeIsActive: false,
		entityId: 'globalDashboard',
		store: {
			id: 'globalDashboard'
		},
		i18nPath: 'globalDashboard'
	},
	import: {
		route: RouteBasePath.StructureImport,
		routeIsActive: true,
		entityId: 'import',
		store: {
			id: 'import',
			sortByProperty: 'createdDate',
			sortOrder: 'desc'
		},
		i18nPath: 'import',
		sidebar: {
			order: 12,
			title: 'import',
			icon: 'icon-cloud-upload',
			isActive: APP_MODE_SETTINGS[ appMode ].sidebar.import.isActive,
			isVisible: APP_MODE_SETTINGS[ appMode ].sidebar.import.isVisible,
		},
		api: {
			path: `${ ApiUrlSegmentEnum.IMPORT }/`,
			endpoint: ApiUrlSegmentEnum.IMPORTS,
			useLocalPath: false,
			useMapping: false,
			mappingOutgoingData: []
		}
	},
	notification: {
		route: RouteBasePath.NOTIFICATIONS,
		routeIsActive: false,
		entityId: 'notifications',
		i18nPath: 'notification',
		store: {
			id: 'notification',
			sortByProperty: 'readDate',
			sortOrder: 'desc'
		}
	},
	myAccount: {
		route: RouteBasePath.MyAccount,
		routeIsActive: true,
		entityId: 'myAccount',
		store: {
			id: 'myAccount',
			sortByProperty: 'lastModifiedDate',
			sortOrder: 'desc'
		},
		i18nPath: 'myAccount',
		sidebar: {
			order: 3,
			title: 'myAccount',
			icon: SvgTypeEnum.MY_ACCOUNT,
			isSvgIcon: true,
			isActive: APP_MODE_SETTINGS[ appMode ].sidebar.myAccount.isActive,
			isVisible: APP_MODE_SETTINGS[ appMode ].sidebar.myAccount.isVisible,
		}
	},
	openArchive: {
		routeIsActive: false,
		entityId: 'openArchive',
		store: {
			id: 'openArchive',
			sortByProperty: 'created',
			sortOrder: 'desc'
		},
		i18nPath: 'openArchive',
		api: {
			path: `${ ApiUrlSegmentEnum.ARCHIVING_API }/`,
			endpoint: ApiUrlSegmentEnum.OPEN_ARCHIVES,
			createOpenArchive: `${ ApiUrlSegmentEnum.ARCHIVING_API }/${ ApiUrlSegmentEnum.BUSINESS_PREMISES }/%businessPremisesId%/${ ApiUrlSegmentEnum.OPEN_ARCHIVES }`
		}
	},
	openArchiveExport: {
		routeIsActive: false,
		entityId: 'openArchiveExport',
		store: {
			id: 'openArchiveExport',
			sortByProperty: 'created',
			sortOrder: 'desc'
		},
		i18nPath: 'openArchiveExport'
	},
	organizations: {
		route: RouteBasePath.Organizations,
		routeIsActive: true,
		entityId: 'organizations',
		store: {
			id: 'organization',
			sortByProperty: 'lastModifiedDate',
			sortOrder: 'desc'
		},
		i18nPath: 'organization',
		sidebar: {
			order: 5,
			title: 'organizations',
			icon: SvgTypeEnum.ORGANIZATION,
			isSvgIcon: true,
			isActive: APP_MODE_SETTINGS[ appMode ].sidebar.organizations.isActive,
			isVisible: APP_MODE_SETTINGS[ appMode ].sidebar.organizations.isVisible,
		},
		api: {
			path: `${ ApiUrlSegmentEnum.MANAGEMENT }/`,
			endpoint: ApiUrlSegmentEnum.ORGANIZATIONS,
			useLocalPath: false,
			useMapping: false,
			mappingOutgoingData: []
		}
	},
	outages: {
		route: RouteBasePath.Outages,
		routeIsActive: false,
		entityId: 'outages',
		store: {
			id: 'outages',
			sortByProperty: 'createdDate',
			sortOrder: 'desc'
		},
		i18nPath: 'outages'
	},
	partnerTenant: {
		route: RouteBasePath.Tenant,
		routeIsActive: false,
		entityId: 'tenants',
		store: {
			id: 'admin-tenant',
			sortByProperty: 'name'
		},
		i18nPath: 'admin',
		sidebar: {
			order: 4,
			title: 'admin',
			icon: 'icon-social-dropbox',
			isActive: APP_MODE_SETTINGS[ appMode ].sidebar.admin.isActive,
			isVisible: APP_MODE_SETTINGS[ appMode ].sidebar.admin.isVisible
		}
	},
	structureExport: {
		route: RouteBasePath.StructureExport,
		routeIsActive: true,
		entityId: 'structureExport',
		store: {
			id: 'structure-export',
			sortByProperty: 'createdDate',
			sortOrder: 'desc'
		},
		i18nPath: 'structureExport',
		sidebar: {
			order: 13,
			title: 'structureExport',
			icon: 'icon-cloud-download',
			isActive: APP_MODE_SETTINGS[ appMode ].sidebar.structureExport.isActive,
			isVisible: APP_MODE_SETTINGS[ appMode ].sidebar.structureExport.isVisible
		},
		api: {
			path: `${ ApiUrlSegmentEnum.EXPORT }/`,
			endpoint: ApiUrlSegmentEnum.EXPORTS,
			useLocalPath: false,
			useMapping: false,
			mappingOutgoingData: []
		}
	},
	taskCenter: {
		routeIsActive: false,
		entityId: 'taskCenter',
		store: {
			id: 'taskCenter'
		},
		i18nPath: 'taskCenter'
	},
	taxations: {
		route: RouteBasePath.Taxations,
		routeIsActive: true,
		entityId: 'taxations',
		i18nPath: 'taxation',
		sidebar: {
			order: 10,
			title: 'taxations',
			icon: SvgTypeEnum.AUTHORITIES_BADGE,
			isSvgIcon: true,
			isActive: APP_MODE_SETTINGS[ appMode ].sidebar.taxations.isActive,
			isVisible: APP_MODE_SETTINGS[ appMode ].sidebar.taxations.isVisible,
		}
	},
	taxationReporting: {
		entityId: 'taxationReporting',
		i18nPath: 'taxation.reporting',
		store: {
			id: 'taxationReporting',
			sortByProperty: 'lastModifiedDate',
			sortOrder: 'desc'
		}
	},
	tenantConsole: {
		route: RouteBasePath.TenantConsole,
		routeIsActive: false,
		i18nPath: 'tenantConsole'
	},
	tenantDashboard: {
		routeIsActive: false,
		entityId: 'tenantDashboard',
		store: {
			id: 'tenantDashboard'
		},
		i18nPath: 'tenantDashboard'
	},
	tenantTransactionDashboard: {
		routeIsActive: false,
		entityId: 'tenantTransactionDashboard',
		store: {
			id: 'tenantTransactionDashboard'
		},
		i18nPath: 'tenantTransactionDashboard'
	},
	tss: {
		route: RouteBasePath.Tss,
		routeIsActive: true,
		entityId: 'tss',
		i18nPath: 'tss',
		store: {
			id: 'tss',
			sortByProperty: 'lastModifiedDate',
			sortOrder: 'desc'
		},
		sidebar: {
			order: 7,
			title: 'tss',
			icon: SvgTypeEnum.TSS,
			isSvgIcon: true,
			isActive: APP_MODE_SETTINGS[ appMode ].sidebar.tss.isActive,
			isVisible: APP_MODE_SETTINGS[ appMode ].sidebar.tss.isVisible
		},
		api: {
			path: `${ ApiUrlSegmentEnum.MANAGEMENT }/`,
			endpoint: ApiUrlSegmentEnum.TSS,
			useLocalPath: false,
			useMapping: false,
			mappingOutgoingData: []
		}
	},
	tssPreInitialization: {
		routeIsActive: false,
		entityId: 'tssPreInitialization',
		i18nPath: 'tssPreInitialization'
	},
	usage: {
		entityId: 'usage',
		i18nPath: 'usage',
		store: {
			archivingUsages: {
				id: 'usageArchiving'
			},
			dtrUsages: {
				id: 'usageDtr'
			},
			fiskalUsages: {
				id: 'usageFiskal'
			},
			gkUsages: {
				id: 'usageGk'
			},
			usageReport: {
				id: 'usageReport'
			}
		}
	},
	users: {
		route: RouteBasePath.Users,
		routeIsActive: true,
		entityId: 'users',
		store: {
			id: 'user',
			sortByProperty: 'lastModifiedDate',
			sortOrder: 'desc'
		},
		i18nPath: 'user',
		sidebar: {
			order: 9,
			title: 'users',
			icon: 'icon-users',
			isActive: APP_MODE_SETTINGS[ appMode ].sidebar.users.isActive,
			isVisible: APP_MODE_SETTINGS[ appMode ].sidebar.users.isVisible
		},
		api: {
			path: `${ ApiUrlSegmentEnum.USER_MANAGEMENT }/`,
			endpoint: ApiUrlSegmentEnum.USERS,
			useLocalPath: false,
			useMapping: false,
			mappingOutgoingData: []
		}
	},
	// DEVELOP
	test: {
		route: RouteBasePath.Test,
		routeIsActive: true,
		sidebar: {
			order: 15,
			title: 'test',
			icon: 'icon-rocket',
			isActive: APP_MODE_SETTINGS[ appMode ].sidebar.test.isActive,
			isVisible: APP_MODE_SETTINGS[ appMode ].sidebar.test.isVisible
		}
	}
};
